import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

/**
 * Optional complement services (Montagem, Tratamento) for the selected variant.
 *
 * @typedef {object} ComplementCartItem
 * @property {string} variantId
 * @property {number} quantity
 */
class ProductModulesComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.addEventListener('change', this.#onDurationChange);
    this.addEventListener('change', this.#onCheckboxChange);
    this.updateSelectedCount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.removeEventListener('change', this.#onDurationChange);
    this.removeEventListener('change', this.#onCheckboxChange);

  }

  /**
   * @param {Event} event
   */
  #onCheckboxChange = (event) => {
    const checkbox = event.target instanceof HTMLElement
      ? event.target.closest('.product-modules__checkbox-input')
      : null;
    if (!(checkbox instanceof HTMLInputElement)) return;

    this.updateSelectedCount();
  };

  updateSelectedCount() {
    const count = this.querySelectorAll(
      '.product-modules__checkbox-input:checked:not(:disabled)'
    ).length;

    const countEl = this.querySelector('[data-product-modules-selected-count]');
    if (countEl) {
      countEl.textContent = String(count);
    }

    this.classList.toggle('product-modules--has-selection', count > 0);
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const blockId = this.dataset.blockId;
    const selector = blockId
      ? `product-modules-component[data-block-id="${blockId}"]`
      : 'product-modules-component';

    const newComponent = event.detail.data.html.querySelector(selector);

    if (!newComponent) {
      this.setAttribute('hidden', '');
      this.replaceChildren();
      this.clearSelections();
      return;
    }

    if (newComponent.hasAttribute('hidden')) {
      this.setAttribute('hidden', '');
    } else {
      this.removeAttribute('hidden');
    }

    morph(this, newComponent, { childrenOnly: true });
    this.clearSelections();
    // morph preserves live input state (checked/value) for matching elements,
    // so a previously-picked warranty duration can survive the re-render even
    // though the server defaults back to the first variant. Force it back in
    // sync explicitly rather than relying on morph to reset it.
    this.resetVariantSelections();
    this.updateSelectedCount();
  };

  /**
   * Handles clicks on the duration/variant radios inside a service card.
   * Keeps the checkbox's data-variant-ids, the displayed price, and the
   * tooltip SKU in sync with whichever variant is currently selected.
   *
   * @param {Event} event
   */
  #onDurationChange = (event) => {
    const radio = event.target instanceof HTMLElement
        ? event.target.closest('.product-modules__variant-radio')
        : null;
    if (!(radio instanceof HTMLInputElement)) return;

    const group = radio.closest('.product-modules__variant-options');
    group?.querySelectorAll('.product-modules__variant-option').forEach((option) => {
        option.classList.toggle('is-selected', option.contains(radio));
    });

    this.#syncVariantSelection(radio);

    // Auto-check the service when a duration is picked
    const checkboxId = radio.dataset.checkboxTarget;
    const checkbox = checkboxId ? this.querySelector(`#${CSS.escape(checkboxId)}`) : null;
    if (checkbox instanceof HTMLInputElement && !checkbox.checked && !checkbox.disabled) {
        checkbox.checked = true;
        this.updateSelectedCount()
    }
  };

  /**
   * Resets every duration selector in this component back to its first
   * (default) option, mirroring clearSelections() for the checkboxes.
   */
  resetVariantSelections() {
    this.querySelectorAll('.product-modules__variant-options').forEach((group) => {
      const radios = group.querySelectorAll('.product-modules__variant-radio');
      const first = radios[0];
      if (!(first instanceof HTMLInputElement)) return;

      radios.forEach((radio) => {
        if (!(radio instanceof HTMLInputElement)) return;
        radio.checked = radio === first;
        radio.closest('.product-modules__variant-option')?.classList.toggle('is-selected', radio === first);
      });

      this.#syncVariantSelection(first);
    });
  }

  /**
   * @param {HTMLInputElement} radio
   */
  #syncVariantSelection(radio) {
    const checkboxId = radio.dataset.checkboxTarget;
    if (!checkboxId) return;

    const checkbox = this.querySelector(`#${CSS.escape(checkboxId)}`);
    if (checkbox instanceof HTMLInputElement) {
      checkbox.dataset.variantIds = radio.value;
    }

    const priceEl = this.querySelector(`[data-product-modules-price="${checkboxId}"]`);
    if (priceEl && radio.dataset.priceFormatted) {
      priceEl.innerHTML = radio.dataset.priceFormatted;
    }

    const skuTrigger = this.querySelector(`[data-product-modules-sku="${checkboxId}"]`);
    if (skuTrigger) {
      skuTrigger.dataset.sku = radio.dataset.sku ?? '';
    }
  }

  /**
   * @param {number} quantity
   * @returns {ComplementCartItem[]}
   */
  getSelectedComplementItems(quantity) {
    /** @type {ComplementCartItem[]} */
    const items = [];
    const checkboxes = this.querySelectorAll('.product-modules__checkbox-input:checked:not(:disabled)');

    checkboxes.forEach((checkbox) => {
      if (!(checkbox instanceof HTMLInputElement)) return;

      const variantIds = checkbox.dataset.variantIds?.split(',').filter(Boolean) ?? [];
      variantIds.forEach((variantId) => {
        items.push({
          variantId: variantId.trim(),
          quantity,
        });
      });
    });

    return items;
  }

  clearSelections() {
    this.querySelectorAll('.product-modules__checkbox-input').forEach((checkbox) => {
      if (checkbox instanceof HTMLInputElement) {
        checkbox.checked = false;
      }
    });
    this.updateSelectedCount()
  }
}

if (!customElements.get('product-modules-component')) {
  customElements.define('product-modules-component', ProductModulesComponent);
}